import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { PagedFilter } from '../../../core/model/paged-filter';
import { PagedResult } from '../../../core/model/paged-result';
import { DataService } from '../../../core/service/base/data.service';
import { cleanV2FilterPayload } from '../../../core/service/base/filter-cleaner';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Article } from '../../model/article';
import { BlockContract } from '../../model/block-contract';
import { CooperativeHousingContract } from '../../model/cooperative-housing-contract';
import { Contract } from '../../model/contract';
import { DwellingContract } from '../../model/dwelling-contract';
import { ParkingContract } from '../../model/parking-contract';
import { PremisesContract } from '../../model/premises-contract';
import { SubleaseStatus } from '../../model/const/enums';

export interface LeaseAgreementListFilterDefinition {
  company: string;
  property: string;
  selectedFields: string[];
  status: number;
  street: string;
  city: string;
  tenantContactId: string;
  tenantContactIdentification: string;
  leaseAgreementNumber: string;
  popularName: string;
  rentalId: string;
  leasePeriodType: number;
  article: string;
  articles: Article[];
  rentFromYear: number;
  rentFromMonth: number;
  terminationMonitoringStatus: number;
  invoiceDeliveryMethod: number;
  leaseAgreementRowIndexClause: number;
  defaultFields: string[];
  subleaseStatus: SubleaseStatus;
  companyCustomPropertiesTextValues: { key: string; value: string }[];
  companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
  companyCustomPropertiesNumericValues: { key: string; value: string }[];
  companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  rentalObjectCustomPropertiesTextValues: { key: string; value: string }[];
  rentalObjectCustomPropertiesDateValues: { key: string; value: LocalDate }[];
  rentalObjectCustomPropertiesNumericValues: { key: string; value: string }[];
  rentalObjectCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  propertyCustomPropertiesTextValues: { key: string; value: string }[];
  propertyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
  propertyCustomPropertiesNumericValues: { key: string; value: string }[];
  propertyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  buildingCustomPropertiesTextValues: { key: string; value: string }[];
  buildingCustomPropertiesDateValues: { key: string; value: LocalDate }[];
  buildingCustomPropertiesNumericValues: { key: string; value: string }[];
  buildingCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  building: string;
}

export abstract class LeaseAgreementListService<TModel extends Contract> extends DataService<TModel> {
  private readonly _defaultFilterFields: string[] = ['company', 'property', 'status', 'tenantContactId', 'rentalId', 'street'];

  protected constructor(
    client: HttpClient,
    config: IAppConfig,
    messageService: MessageService,
    modelConstructor: { new (): TModel },
    progressBar: ProgressBarService,
    private readonly categorySegment: string
  ) {
    super(client, config, messageService, modelConstructor, progressBar, 'lease-agreement-list');
  }

  getFilterDefinition(): Observable<LeaseAgreementListFilterDefinition> {
    return this.client.get<PagedFilter<any>>(`${this.endpoint}/${this.categorySegment}/filter`).pipe(
      map(res => {
        const filter = res?.filter ?? {};
        const selectedFields = Array.isArray(filter.selectedFields)
          ? filter.selectedFields.map(field => (field === 'tenant' ? 'tenantContactId' : field))
          : this._defaultFilterFields;

        return {
          company: filter.company ? filter.company : '',
          property: filter.property ? filter.property : '',
          selectedFields: selectedFields,
          status: filter.status ? filter.status : 0,
          street: filter.street ? filter.street : '',
          city: filter.city ? filter.city : '',
          tenantContactId: filter.tenantContactId ? filter.tenantContactId : '',
          tenantContactIdentification: filter.tenantContactIdentification ? filter.tenantContactIdentification : '',
          leaseAgreementNumber: filter.leaseAgreementNumber ? filter.leaseAgreementNumber : '',
          popularName: filter.popularName ? filter.popularName : '',
          rentalId: filter.rentalId ? filter.rentalId : '',
          leasePeriodType: filter.leasePeriodType ? filter.leasePeriodType : null,
          article: filter.article ? filter.article : '',
          articles: Array.isArray(filter.articles) ? filter.articles.map(x => Object.assign(new Article(), x)) : [],
          rentFromYear: LocalDate.today().year,
          rentFromMonth: LocalDate.today().month,
          terminationMonitoringStatus: filter.terminationMonitoringStatus ? filter.terminationMonitoringStatus : 0,
          invoiceDeliveryMethod: filter.invoiceDeliveryMethod || '',
          leaseAgreementRowIndexClause: filter.leaseAgreementRowIndexClause ? filter.leaseAgreementRowIndexClause : null,
          defaultFields: this._defaultFilterFields,
          subleaseStatus: filter.subleaseStatus ? filter.subleaseStatus : 0,
          companyCustomPropertiesTextValues: Array.isArray(filter.companyCustomPropertiesTextValues)
            ? filter.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(filter.companyCustomPropertiesNumericValues)
            ? filter.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(filter.companyCustomPropertiesDateValues)
            ? filter.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(filter.companyCustomPropertiesBooleanValues)
            ? filter.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          rentalObjectCustomPropertiesTextValues: this.mapCustomPropertyFilterValues(filter.rentalObjectCustomPropertiesTextValues),
          rentalObjectCustomPropertiesNumericValues: this.mapCustomPropertyFilterValues(filter.rentalObjectCustomPropertiesNumericValues),
          rentalObjectCustomPropertiesDateValues: this.mapCustomPropertyFilterValues(filter.rentalObjectCustomPropertiesDateValues),
          rentalObjectCustomPropertiesBooleanValues: this.mapCustomPropertyFilterValues(filter.rentalObjectCustomPropertiesBooleanValues),
          propertyCustomPropertiesTextValues: this.mapCustomPropertyFilterValues(filter.propertyCustomPropertiesTextValues),
          propertyCustomPropertiesNumericValues: this.mapCustomPropertyFilterValues(filter.propertyCustomPropertiesNumericValues),
          propertyCustomPropertiesDateValues: this.mapCustomPropertyFilterValues(filter.propertyCustomPropertiesDateValues),
          propertyCustomPropertiesBooleanValues: this.mapCustomPropertyFilterValues(filter.propertyCustomPropertiesBooleanValues),
          buildingCustomPropertiesTextValues: this.mapCustomPropertyFilterValues(filter.buildingCustomPropertiesTextValues),
          buildingCustomPropertiesNumericValues: this.mapCustomPropertyFilterValues(filter.buildingCustomPropertiesNumericValues),
          buildingCustomPropertiesDateValues: this.mapCustomPropertyFilterValues(filter.buildingCustomPropertiesDateValues),
          buildingCustomPropertiesBooleanValues: this.mapCustomPropertyFilterValues(filter.buildingCustomPropertiesBooleanValues),
          building: filter.building ? filter.building : '',
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  override prepareFilter(filter: any): any {
    return cleanV2FilterPayload(super.prepareFilter(filter));
  }

  protected override createModel(data: any): TModel {
    data.article ??= {};
    data.articleGroup ??= {};
    data.companyCustomPropertiesTextValues ??= {};
    data.companyCustomPropertiesDateValues ??= {};
    data.companyCustomPropertiesNumericValues ??= {};
    data.companyCustomPropertiesBooleanValues ??= {};
    data.rentalObjectCustomPropertiesTextValues ??= {};
    data.rentalObjectCustomPropertiesDateValues ??= {};
    data.rentalObjectCustomPropertiesNumericValues ??= {};
    data.rentalObjectCustomPropertiesBooleanValues ??= {};
    data.propertyCustomPropertiesTextValues ??= {};
    data.propertyCustomPropertiesDateValues ??= {};
    data.propertyCustomPropertiesNumericValues ??= {};
    data.propertyCustomPropertiesBooleanValues ??= {};
    data.buildingCustomPropertiesTextValues ??= {};
    data.buildingCustomPropertiesDateValues ??= {};
    data.buildingCustomPropertiesNumericValues ??= {};
    data.buildingCustomPropertiesBooleanValues ??= {};

    return super.createModel(data);
  }

  private mapCustomPropertyFilterValues(values: { key: string; value: any }[]): { key: string; value: any }[] {
    return Array.isArray(values) ? values.map(v => ({ key: v.key, value: v.value })) : [];
  }

  override getPagedList(filter: any, page: number, pageSize: number, order: string, descending: boolean): Observable<PagedResult<TModel>> {
    this.progressBar.start();
    filter = this.prepareFilter(filter);

    return this.client
      .post<PagedResult<TModel>>(`${this.endpoint}/${this.categorySegment}/page`, {
        filter,
        page,
        pageSize,
        orderBy: order,
        descending,
      })
      .pipe(
        map(response => {
          const result = Object.assign(new PagedResult<TModel>(), response);
          result.items = this.toModels(response.items);
          return result;
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  exportFiltered(
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    affectedItemsCount: number,
    fileName: string
  ): Observable<File> {
    return this.exportDataItemsFilteredWithUrl(
      null,
      `${this.categorySegment}/export-filter`,
      filter,
      includedIds,
      excludedIds,
      affectedItemsCount,
      fileName,
      true
    );
  }

  exportFilteredNoPersonalData(
    filter: any,
    includedIds: string[],
    excludedIds: string[],
    affectedItemsCount: number,
    fileName: string
  ): Observable<File> {
    return this.exportDataItemsFilteredWithUrl(
      null,
      `${this.categorySegment}/export-filter-no-personal-data`,
      filter,
      includedIds,
      excludedIds,
      affectedItemsCount,
      fileName,
      true
    );
  }

  getDynamicExcelExport(
    filter: any,
    context: string,
    fileName: string,
    selectedListViewColumns?: string[],
    includedIds?: string[],
    excludedIds?: string[],
    affectedItemsCount?: number
  ): Observable<File> {
    this.progressBar.start();

    const model = {
      filter: {
        context,
        fileName,
        selectedListViewColumns,
        typedFilter: this.prepareFilter(filter),
      },
      includedIds,
      excludedIds,
      affectedItemsCount,
    };

    return this.client
      .post(`${this.endpoint}/${this.categorySegment}/excel-export`, model, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => this.toFile(res, `${fileName}.xlsx`)),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }
}

@Injectable()
export class DwellingLeaseAgreementListService extends LeaseAgreementListService<DwellingContract> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, DwellingContract, progressBar, 'dwelling');
  }
}

@Injectable()
export class BlockLeaseAgreementListService extends LeaseAgreementListService<BlockContract> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, BlockContract, progressBar, 'block');
  }
}

@Injectable()
export class CooperativeHousingLeaseAgreementListService extends LeaseAgreementListService<CooperativeHousingContract> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, CooperativeHousingContract, progressBar, 'co-housing');
  }
}

@Injectable()
export class ParkingLeaseAgreementListService extends LeaseAgreementListService<ParkingContract> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, ParkingContract, progressBar, 'parking');
  }
}

@Injectable()
export class PremisesLeaseAgreementListService extends LeaseAgreementListService<PremisesContract> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, PremisesContract, progressBar, 'premises');
  }
}
